
/* ============================== src/api/queries/projects.js ============================== */
/**
 * Хуки проектов.
 *
 * GET /projects и GET /projects/lookup возвращают постраничный конверт,
 * а не массив, поэтому во всех хуках выборки списков применяется общий
 * селектор unwrapProjectsList, который приводит ответ к массиву
 * проектов для потребителей (MainLayout, useDb, useDataHelpers и т.д.).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { api } from "../client";

export const PROJECTS_KEY = ["projects"];

const unwrapProjectsList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.items)) return raw.items;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.projects)) return raw.projects;
  return [];
};

export function useProjectsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, params],
    queryFn: () => api.get("/projects", { params }),
    select: unwrapProjectsList,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useProjectLookupQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: ["projects", "lookup", params],
    queryFn: () => api.get("/projects/lookup", { params }),
    select: unwrapProjectsList,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useProjectQuery(id, options = {}) {
  return useQuery({
    queryKey: ["project", id],
    queryFn: () => api.get(`/projects/${id}`),
    enabled: !!id,
    ...options,
  });
}

export function useCreateProjectMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/projects", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useUpdateProjectMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, needsConfirmation, version }) => {
      const headers = {};
      if (needsConfirmation) headers["X-Needs-Confirmation"] = "true";
      if (version !== undefined && version !== null)
        headers["If-Match"] = String(version);
      return api.patch(`/projects/${id}`, body, { headers });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useDeleteProjectMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/projects/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useDelegationProjectsQuery(userId, options = {}) {
  return useQuery({
    queryKey: ["projects", "delegation", userId],
    queryFn: () =>
      api.get("/projects", {
        params: { managerId: userId, limit: 500 },
      }),
    select: unwrapProjectsList,
    enabled: !!userId,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useRestoreProjectMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/projects/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROJECTS_KEY });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
